"use client"

import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { Lightbulb, Target, Compass, Rocket, ChevronDown, ChevronLeft, ChevronRight, ArrowUp, Gamepad2, Volume2, VolumeX, Check, Mail, CircleCheck } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import Navigation, { Footer } from "./Navigation"
import DesignAdventureApp from "./App"
import { usePasswordCheck } from "./hooks/usePasswordCheck"
import { useSeo } from "./hooks/useSeo"

import MissiesBanner from "./components/MissiesBanner"
import MissieDemo, { MissieDemoHandle } from "./components/MissieDemo"
import "./css/AboutPage.css"
import "./css/GameSelector.css"
import "./css/PricingSection.css"
import "./css/MissieDemo.css"



const LandingPage = () => {
  // Get current language from context
  const { language } = useLanguage()


  // State for app configuration and about content
  const [appConfig, setAppConfig] = useState<any>(null)
  const [aboutContent, setAboutContent] = useState<any>(null)
  const [landingContent, setLandingContent] = useState<any>(null)
  const [openFaqId, setOpenFaqId] = useState<string | null>("how-free-trial-works")
  const [isLoading, setIsLoading] = useState(true)
  const [activeHowItWorksStep, setActiveHowItWorksStep] = useState(0)
  const [howItWorksAutoplayPaused, setHowItWorksAutoplayPaused] = useState(false)
  // Autoplay only runs while the section is on screen and the tab is
  // visible (#434). Each step change rebuilds a whole game iframe, so
  // letting it cycle unseen (scrolled past, background tab) cost every
  // visitor data and battery for nothing.
  // State (not useRef) so the observer below re-attaches whenever the
  // section mounts: it's hidden behind the loader for a while after
  // aboutContent arrives, and again on a language switch.
  const [howItWorksEl, setHowItWorksEl] = useState<HTMLElement | null>(null)
  const [howItWorksOnScreen, setHowItWorksOnScreen] = useState(false)
  const [pageVisible, setPageVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  )
  // Starts muted - autoplay only works reliably across browsers when muted,
  // so this has to be the default; the toggle just lets someone turn sound
  // on if they want it, not the other way around.
  const [tipVideoMuted, setTipVideoMuted] = useState(true)
  // The mobile "tap to play" callout lives outside MissieDemo (it's a
  // callout pointing at the phone from the card below it, not something
  // drawn over the preview itself), so opening the game demo's own
  // full-screen modal from there goes through this ref instead of local
  // state. The tip video has no such component to reach into - it gets its
  // own small modal further down, reusing MissieDemo's modal styling so all
  // three steps go full-screen the same way (Laura: "all three needs it as
  // the video is also better full screen").
  const missieDemoRef = useRef<MissieDemoHandle>(null)
  const [tipVideoModalOpen, setTipVideoModalOpen] = useState(false)

  const bannerImage3 = aboutContent?.banner3?.image


  // Helper function to get localized text from content
  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText

    const keys = path.split(".")
    let value: any = appConfig

    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    if (value && typeof value === "object" && value[language.code]) {
      return value[language.code]
    }

    return typeof value === "string" ? value : value?.en || defaultText
  }

  const L = (obj: any, fallback = "") => {
    if (!obj) return fallback
    if (typeof obj === "string") return obj
    return obj?.[language.code] || obj?.nl || obj?.en || fallback
  }

  useSeo({
    path: "/",
    title:
      language.code === "en"
        ? "Techtok Missions | A digital-skills game for kids aged 8-14"
        : "Techtok Missies | Digitale vaardigheden voor kinderen, als game",
    description:
      language.code === "en"
        ? "The game where kids aged 8 to 14 learn the digital skills that designers, coders and IT professionals use every day. From scrolling to creating."
        : "De game waarin kinderen van 8 tot 14 de digitale vaardigheden leren die ontwerpers, programmeurs en ICT'ers elke dag gebruiken. Van scrollen naar creëren.",
  })

  // Load app configuration and content
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, false)
        setAppConfig(config)

        // Apply accent color if provided
        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }

        // Load the about content
        const aboutResponse = await fetch(`/content/main/app-about.json`)
        if (aboutResponse.ok) {
          const aboutData = await aboutResponse.json()
          setAboutContent(aboutData)
        }

        // Load the landing content (for dynamic pricing, etc.)
        const landingResponse = await fetch(`/content/main/app-landing.json`)
        if (landingResponse.ok) {
          const landingData = await landingResponse.json()
          setLandingContent(landingData)
        }
      } catch (error) {
        console.error("Failed to load data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [language.code])

  // Auto-play: cycles through the three "how it works" steps on a timer, on
  // both mobile and desktop - restarts cleanly whenever the active step
  // changes (a manual click/arrow tap included), and pauses while someone's
  // hovering the section so it doesn't yank the desktop inline game or a
  // playing video out from under them mid-interaction.
  useEffect(() => {
    const steps = aboutContent?.howItWorks?.steps
    if (!steps || steps.length < 2 || howItWorksAutoplayPaused || !howItWorksOnScreen || !pageVisible) return
    const timer = setInterval(() => {
      setActiveHowItWorksStep((current) => (current + 1) % steps.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [aboutContent?.howItWorks?.steps, howItWorksAutoplayPaused, howItWorksOnScreen, pageVisible, activeHowItWorksStep])

  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState === "visible")
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [])

  useEffect(() => {
    const el = howItWorksEl
    if (!el) {
      setHowItWorksOnScreen(false)
      return
    }
    if (typeof IntersectionObserver === "undefined") {
      setHowItWorksOnScreen(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setHowItWorksOnScreen(entry.isIntersecting),
      { threshold: 0.25 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [howItWorksEl])

  // Same iOS-safe body-scroll-lock technique as MissieDemo's own modal
  // (plain overflow:hidden leaks touch-scroll through on Safari) - this
  // modal reuses MissieDemo's modal CSS classes, but it's the tip video,
  // not a MissieDemo instance, so it needs its own copy of the lock.
  useEffect(() => {
    if (!tipVideoModalOpen) return
    const scrollY = window.scrollY
    const body = document.body.style
    const prev = { position: body.position, top: body.top, width: body.width, overflow: body.overflow }
    body.position = "fixed"
    body.top = `-${scrollY}px`
    body.width = "100%"
    body.overflow = "hidden"
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTipVideoModalOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => {
      body.position = prev.position
      body.top = prev.top
      body.width = prev.width
      body.overflow = prev.overflow
      window.scrollTo(0, scrollY)
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [tipVideoModalOpen])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Map section icons based on index
  const getSectionIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Lightbulb />
      case 1:
        return <Target />
      case 2:
        return <Compass />
      default:
        return <Lightbulb />
    }
  }



  // Map section icons based on index
  const getMotivationIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Rocket />
      case 1:
        return <Lightbulb />
      case 2:
        return <Compass />
      default:
        return <Lightbulb />
    }
  }

  // Handle navigation
  const handleNavigation = (page: string) => {}

  // No lesson grid here any more (#415): the home page sells Missies to
  // parents; the classroom lessons live on /teachers.
  return (
        <div className="landing-selector-container">
          <Navigation appConfig={appConfig} currentPage="home" onNavigate={handleNavigation} t={t} />

          <main className="main-content">
        {/* The page opens on the Missies banner (#415). "Van scrollen naar
            creëren" (heroSection.title, the old hero) is its hook label; the
            "Nieuw" label and the line pointing down to the lessons are off
            here, the lessons live on /teachers now. */}
        <MissiesBanner
          data={aboutContent?.missiesBanner && {
            ...aboutContent.missiesBanner,
            eyebrow: t("heroSection.title", language.code === "nl" ? "Van scrollen naar creëren" : "From scrolling to creating"),
            bridgeText: undefined,
            trialButton: { nl: "Probeer 7 dagen gratis", en: "Try 7 days free" },
            trialLink: "/portal/register.php",
          }}
          languageCode={language.code}
          atTop
        />

        {/* How it works: 3-step section + live demo */}
        {aboutContent?.howItWorks && (() => {
          const activeStepData = aboutContent.howItWorks.steps[activeHowItWorksStep]
          const isVideo = !!activeStepData?.tipVideo
          const conceptId = activeStepData?.conceptId || "app-basics"
          return (
          <section className="how-it-works-section" ref={setHowItWorksEl}>
            <div className="how-it-works-inner">
              <h2 className="how-it-works-title">
                {aboutContent.howItWorks.title?.[language.code] || "Zo word je een digitale maker"}
              </h2>
              <a href="/missies" className="how-it-works-link">
                {language.code === "nl" ? "Meer over Techtok Missies →" : "More about Techtok Missies →"}
              </a>
              <div
                className="how-it-works-layout"
                onMouseEnter={() => setHowItWorksAutoplayPaused(true)}
                onMouseLeave={() => setHowItWorksAutoplayPaused(false)}
              >
                <div className="how-it-works-carousel">
                  <button
                    type="button"
                    className="how-it-works-arrow how-it-works-arrow-prev"
                    onClick={() =>
                      setActiveHowItWorksStep(
                        (activeHowItWorksStep - 1 + aboutContent.howItWorks.steps.length) %
                          aboutContent.howItWorks.steps.length
                      )
                    }
                    aria-label={language.code === "nl" ? "Vorige stap" : "Previous step"}
                  >
                    <ChevronLeft />
                  </button>
                  <div className="how-it-works-grid">
                    {aboutContent.howItWorks.steps?.map((step: any, index: number) => (
                      <button
                        key={index}
                        type="button"
                        className={`how-it-works-card${index === activeHowItWorksStep ? " active" : ""}`}
                        onClick={() => setActiveHowItWorksStep(index)}
                        aria-pressed={index === activeHowItWorksStep}
                      >
                        <span className="how-it-works-number">{index + 1}</span>
                        <div className="how-it-works-icon">
                          {index === 0 ? <Gamepad2 /> : index === 1 ? <Lightbulb /> : <Rocket />}
                        </div>
                        <h3 className="how-it-works-heading">{step.heading?.[language.code] || ""}</h3>
                        <p className="how-it-works-content">{step.content?.[language.code] || ""}</p>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="how-it-works-arrow how-it-works-arrow-next"
                    onClick={() => setActiveHowItWorksStep((activeHowItWorksStep + 1) % aboutContent.howItWorks.steps.length)}
                    aria-label={language.code === "nl" ? "Volgende stap" : "Next step"}
                  >
                    <ChevronRight />
                  </button>

                  {/* Mobile-only callout: a small labeled button sitting
                      just above the top-right corner of this box, replacing
                      the old dark-wash-plus-icon treatment drawn over the
                      preview itself (Laura found that "weird... under the
                      card", and the hand-drawn arrow "really weird").
                      Opens the same full-screen treatment for all three
                      steps - the game demo via MissieDemo's own ref, the
                      tip video via its own small modal just below. */}
                  <button
                    type="button"
                    className="how-it-works-tap-hint"
                    onClick={() => (isVideo ? setTipVideoModalOpen(true) : missieDemoRef.current?.open())}
                  >
                    <ArrowUp size={14} strokeWidth={2.5} />
                    {language.code === "nl" ? "Tik om te spelen" : "Tap to play"}
                  </button>
                </div>

                <div className="how-it-works-demo">
                  <p className="how-it-works-demo-label">
                    {language.code === "nl" ? "Probeer het zelf" : "Try it yourself"}
                  </p>
                  {/* A step can be a real playable demo (concept, or
                      straight into its Makerij via demoMode:"creation") or
                      an actual short video (tipVideo) - "los problemen op"
                      is a real ~45s clip, not a live game screen. */}
                  {isVideo ? (
                    <div className="how-it-works-tip-video-wrap">
                      <video
                        key={activeStepData.tipVideo}
                        className="how-it-works-tip-video"
                        src={activeStepData.tipVideo}
                        poster={activeStepData.tipPoster}
                        autoPlay
                        muted={tipVideoMuted}
                        loop
                        playsInline
                      />
                      <button
                        type="button"
                        className="how-it-works-tip-mute-btn"
                        onClick={() => setTipVideoMuted((m) => !m)}
                        aria-label={
                          tipVideoMuted
                            ? language.code === "nl" ? "Geluid aanzetten" : "Turn sound on"
                            : language.code === "nl" ? "Geluid uitzetten" : "Turn sound off"
                        }
                      >
                        {tipVideoMuted ? <VolumeX /> : <Volume2 />}
                      </button>
                    </div>
                  ) : (
                    // key forces a fresh iframe (and demo state) per step
                    // rather than reusing one mid-interaction from a
                    // different concept.
                    <MissieDemo
                      ref={missieDemoRef}
                      key={conceptId}
                      conceptId={conceptId}
                      loop={1}
                      mode={activeStepData?.demoMode === "creation" ? "creation" : "concept"}
                      playHint="edge"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Full-screen tip video, opened from the callout above - reuses
                MissieDemo's own modal styling so all three steps go
                full-screen the same consistent way. */}
            {tipVideoModalOpen &&
              createPortal(
                <div
                  className="missie-demo-modal-backdrop"
                  role="dialog"
                  aria-modal="true"
                  onClick={() => setTipVideoModalOpen(false)}
                >
                  <div className="missie-demo-modal" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="missie-demo-modal-close"
                      onClick={() => setTipVideoModalOpen(false)}
                      aria-label="Sluiten"
                    >
                      ✕
                    </button>
                    <div className="missie-demo-modal-frame">
                      <video
                        className="missie-demo-modal-iframe"
                        src={activeStepData?.tipVideo}
                        poster={activeStepData?.tipPoster}
                        autoPlay
                        muted={tipVideoMuted}
                        loop
                        playsInline
                        controls={false}
                      />
                      <button
                        type="button"
                        className="how-it-works-tip-mute-btn"
                        onClick={() => setTipVideoMuted((m) => !m)}
                        aria-label={
                          tipVideoMuted
                            ? language.code === "nl" ? "Geluid aanzetten" : "Turn sound on"
                            : language.code === "nl" ? "Geluid uitzetten" : "Turn sound off"
                        }
                      >
                        {tipVideoMuted ? <VolumeX /> : <Volume2 />}
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}
          </section>
          )
        })()}

        {/* Pricing Section: one line saying every plan starts with the free
            week, two cards side by side (Founding Families highlighted on the
            left, then 1 year), then how paying works. All copy lives in
            public/content/main/app-landing.json. */}
        <section className="pricing-section" id="pricing">
          <div className="pricing-header">
            <h2 className="pricing-title">
              {L(landingContent?.pricing?.title, language.code === "nl" ? "Eén keer betalen. Jij bepaalt hoe lang." : "Pay once. You decide how long.")}
            </h2>
            <p className="pricing-subtitle">
              {L(landingContent?.pricing?.subtitle, "")}
            </p>
            {landingContent?.pricing?.trial && (
              <p className="pricing-trial">
                <span className="pricing-trial-icon"><Gamepad2 /></span>
                <span>
                  <strong>{L(landingContent.pricing.trial.title, "")}</strong>{" "}
                  {L(landingContent.pricing.trial.text, "")}
                </span>
              </p>
            )}
          </div>

          <div className="pricing-plans">
          <div className="pricing-grid">
            {[
              // Founding Families stays its own `promo` entry in the JSON
              // (privacy-terms.php reads pricing.plans), but shows as the
              // first card, highlighted.
              ...(landingContent?.pricing?.promo ? [{ ...landingContent.pricing.promo, highlight: true }] : []),
              ...(landingContent?.pricing?.plans || []),
            ].map((plan: any) => (
              <div key={plan.id} className={`pricing-card ${plan.featured ? "featured" : ""} ${plan.highlight ? "highlight" : ""}`}>
                {plan.badge && (
                  <div className="pricing-badge">{L(plan.badge, "")}</div>
                )}
                <h3 className="pricing-card-title">{L(plan.title, "")}</h3>
                <p className="pricing-card-desc">{L(plan.description, "")}</p>
                <div className="pricing-price">
                  <span className="pricing-price-currency">{plan.currency || "€"}</span>
                  {plan.price}
                  {plan.period && <span className="pricing-price-period">{L(plan.period, "")}</span>}
                </div>
                {plan.access && <div className="pricing-price-access">{L(plan.access, "")}</div>}
                <div className="pricing-price-tax">{L(plan.tax, "incl. 21% btw")}</div>
                <ul className="pricing-features">
                  {(plan.features || []).map((feat: any, idx: number) => {
                    const text = L(feat, "")
                    return (
                      <li key={idx}>
                        <Check />
                        {feat.highlight ? <strong>{text}</strong> : text}
                      </li>
                    )
                  })}
                </ul>
                <a
                  href={plan.button?.href || "/portal/register.php"}
                  className={`pricing-button ${plan.featured || plan.highlight ? "featured-button" : ""}`}
                >
                  {L(plan.button?.text, "Aan de slag")}
                </a>
                {plan.button?.note && <div className="pricing-button-note">{L(plan.button.note, "")}</div>}
              </div>
            ))}
          </div>

          {landingContent?.pricing?.steps && (
            <div className="pricing-steps">
              <ol className="pricing-steps-list">
                {landingContent.pricing.steps.map((step: any, idx: number, steps: any[]) => {
                  const StepIcon = [Gamepad2, Mail, CircleCheck][idx] || CircleCheck
                  const isLast = idx === steps.length - 1
                  return (
                    <li key={idx} className={`pricing-step ${isLast ? "is-done" : ""}`}>
                      <span className="pricing-step-icon"><StepIcon /></span>
                      <div className="pricing-step-body">
                        {step.when && <span className="pricing-step-when">{L(step.when, "")}</span>}
                        <strong className="pricing-step-title">{L(step.title, "")}</strong>
                        <span className="pricing-step-text">{L(step.text, "")}</span>
                      </div>
                    </li>
                  )
                })}
              </ol>
            </div>
          )}
          </div>


          <div className="pricing-footer">
            {language.code === "nl" ? "School of klas? " : "School or class? "}
            <a href="mailto:hallo@techtok.me">
              {language.code === "nl" ? "Neem contact op voor een klaslicentie (vanaf €249/jaar)" : "Contact us for a class license (from €249/year)"}
            </a>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="faq-header">
            <h2 className="faq-title">
              {L(landingContent?.faq?.title, language.code === "nl" ? "Veelgestelde Vragen" : "Frequently Asked Questions")}
            </h2>
            <p className="faq-subtitle">
              {L(landingContent?.faq?.subtitle, language.code === "nl" ? "Alles wat ouders willen weten over Techtok, veilig leren en onze plannen." : "Everything parents want to know about Techtok, safe learning, and our plans.")}
            </p>
          </div>

          <div className="faq-list">
            {(landingContent?.faq?.items || [
              {
                id: "how-free-trial-works",
                question: {
                  nl: "Hoe werkt de gratis proefweek?",
                  en: "How does the 1-week free trial work?"
                },
                answer: {
                  nl: "Je kind kan 7 dagen direct en gratis alles spelen. Geen betaalgegevens vooraf, geen abonnement en geen automatische verlenging. Je kunt alles vrijblijvend ontdekken.",
                  en: "Your child can play everything, instantly and free, for 7 days. No payment details required upfront, no subscription, and no automatic renewal. You can explore everything with zero obligations."
                }
              },
              {
                id: "target-age",
                question: {
                  nl: "Voor welke leeftijd is Techtok geschikt?",
                  en: "What age is Techtok suitable for?"
                },
                answer: {
                  nl: "Techtok is speciaal ontwikkeld voor kinderen van 8 t/m 14 jaar. De missies zijn stap-voor-stap opgebouwd met vrolijke audio-ondersteuning, visuele puzzels en duidelijke uitleg, zodat kinderen zelfstandig of gezellig samen met een ouder kunnen spelen.",
                  en: "Techtok is specifically designed for kids aged 8 to 14. Missions are structured step-by-step with playful audio support, visual puzzles, and friendly guidance so kids can learn independently or together with a parent."
                }
              },
              {
                id: "safety-privacy",
                question: {
                  nl: "Is Techtok veilig en reclamevrij?",
                  en: "Is Techtok safe and ad-free?"
                },
                answer: {
                  nl: "100% veilig. We tonen géén advertenties, verkopen nooit gegevens en voldoen strikt aan de Europese privacywetgeving (AVG/GDPR). Kinderen gebruiken in Techtok geen AI-tools: wat je kind typt of tekent, sturen wij nooit naar een AI-dienst.",
                  en: "100% safe. We show zero advertisements, never sell data, and adhere strictly to European privacy guidelines (AVG/GDPR). Kids don't use any AI tools in Techtok: we never send what your child types or draws to an AI service."
                }
              },
              {
                id: "prior-coding-experience",
                question: {
                  nl: "Heeft mijn kind voorkennis nodig van programmeren?",
                  en: "Does my child need prior coding experience?"
                },
                answer: {
                  nl: "Helemaal niet! We beginnen bij de basis met speelse visuele concepten, interactieve uitdagingen en creatieve verhalen. Of je kind nu nieuwsgierig is naar games, verhalen of code, iedereen kan direct instappen.",
                  en: "Not at all! We start from scratch with playful visual concepts, interactive challenges, and creative storytelling. Whether your child is curious about games, stories, or coding, everyone can jump right in."
                }
              },
              {
                id: "after-trial-family-access",
                question: {
                  nl: "Wat gebeurt er na de proefweek, en wat is het verschil tussen de plannen?",
                  en: "What happens after the trial, and what's the difference between the plans?"
                },
                answer: {
                  nl: "Na 7 dagen kies je wat bij jullie past: Founding Families voor eenmalig €19 (voor de eerste 50 gezinnen die feedback willen geven) of 1 jaar spelen voor eenmalig €39. Allebei geven 12 maanden toegang. We sturen een veilige betaallink per e-mail. Je betaalt één keer en na afloop stopt je toegang vanzelf: geen abonnement, geen verrassingen.",
                  en: "After 7 days, choose what fits your family: Founding Families for a one-time €19 (for the first 50 families who want to give feedback) or 1 year of play for a one-time €39. Both give 12 months of access. We send a secure payment link by email. You pay once, and when your time is up, access simply ends: no subscription, no surprise charges."
                }
              }
            ]).map((faq: any) => {
              const isOpen = openFaqId === faq.id
              return (
                <div key={faq.id} className={`faq-item ${isOpen ? "open" : ""}`}>
                  <button
                    type="button"
                    className="faq-question-btn"
                    onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                    aria-expanded={isOpen}
                  >
                    <span>{L(faq.question, "")}</span>
                    <span className="faq-chevron">
                      <ChevronDown size={18} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer">
                      <p>{L(faq.answer, "")}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

     



        {/* Motivation Section */}
        <section className="motivation-section">
          <section className="hero-section">
            <div className="hero-content">
              <div className="hero-content-inner">
                <h1 className="hero-title">
                  {aboutContent?.about?.motivation?.[language.code] || "Every child ready for the digital future"}
                </h1>
                {aboutContent?.about?.motivationSubtitle?.[language.code] && (
                  <p className="hero-subtitle">{aboutContent.about.motivationSubtitle[language.code]}</p>
                )}
              </div>
            </div>
          </section>

          <section className="about-main">
            <div className="about-sections">
              {aboutContent?.motivations?.map((section, index) => (
                <section key={index} className="about-section">
                  <div className="section-icon">{getSectionIcon(index)}</div>
                  <h2 className="section-heading">{section.heading?.[language.code] || `Section ${index + 1}`}</h2>
                  <p className="section-content">{section.content?.[language.code] || ""}</p>
                </section>
              ))}
            </div>
          </section>
        </section>

        {aboutContent?.team && (
          <section className="team-main">
            <div
              className="relative top-8 left-12 w-8 h-8 rounded-full opacity-60 animate-bounce"
              style={{ backgroundColor: "#FAFF05", animationDelay: "0s", animationDuration: "3s" }}
            ></div>
            <div
              className="relative top-16 right-20 w-6 h-6 rounded-full opacity-50 animate-bounce"
              style={{ backgroundColor: "#FCFF6C", animationDelay: "1s", animationDuration: "2.5s" }}
            ></div>
            <div
              className="absolute bottom-20 left-1/4 w-4 h-4 rounded-full opacity-70 animate-bounce"
              style={{ backgroundColor: "#8CFFCF", animationDelay: "0.5s", animationDuration: "3.5s" }}
            ></div>
            <div
              className="absolute top-1/3 right-8 w-5 h-5 opacity-40 animate-spin"
              style={{
                backgroundColor: "#AA8DFF",
                borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                animationDuration: "4s",
              }}
            ></div>

            <section className="team-section">
              <div className="hero-content">
                <div className="hero-content-inner">
                  <h1 className="hero-title">{aboutContent.about.team?.[language.code] || "The team"}</h1>
                  <p className="hero-subtitle">{t("heroSection.teamSubheader", "De creatieve koppen achter Techtok Academy!")}</p>
                </div>
              </div>
            </section>

            <div className="about-me-container">
              <div className="about-me-content" style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                {aboutContent.team.map((person, idx) => (
                  <div key={person.title?.[language.code]} style={{ flex: "1 1 320px", maxWidth: 450 }}>
                    <div className="about-me-image-container">
                      <img
                        src={person.image || "/placeholder.svg"}
                        alt={person.title?.[language.code]}
                        className="about-me-image"
                      />
                    </div>
                    <div className="about-me-text">
                      <h3 className="about-me-title">{person.title?.[language.code]}</h3>
                      <h4 className="about-me-subtitle">{person.subtitle?.[language.code]}</h4>
                      <p className="about-me-description">{person.content?.[language.code]}</p>
                      {person.linkUrl && (
                        <a href={person.linkUrl} target="_blank" rel="noopener noreferrer" className="about-me-link">
                          {person.linkText?.[language.code]}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: "center", marginTop: "2rem", marginBottom: "4rem", position: "relative", zIndex: 1 }}>
                <a href="/about" className="about-me-link">
                  {language.code === "nl" ? "Lees ons volledige verhaal" : "Read our full story"}
                </a>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Exit / contact section - kept identical to the About page's closing
          CTA (src/AboutPage.tsx) so both pages end the same way. */}
      <div className="exit-banner">
        <div className="coming-soon-section">
          <h1 className="hero-title">{aboutContent.contact.heading?.[language.code] || "Get in Touch"}</h1>
          <p className="hero-subtitle">
            {t(
              "comingSoon.contact",
              language.code === "nl"
                ? "Start met een gratis proefweek, kies daarna het plan dat bij jullie past. Geen abonnement, geen kleine lettertjes."
                : "Start with a free trial week, then pick the plan that fits your family. No subscription, no hidden fees.",
            )}
          </p>

          <a href="/portal/register.php" className="main-action-button">
            {language.code === "nl" ? "Probeer 7 dagen gratis" : "Try 7 days free"}
          </a>
          <p className="exit-banner-note">
            {language.code === "nl" ? "Geen betaalgegevens nodig" : "No payment details needed"}
          </p>
          <p className="about-contact-email">
            {language.code === "nl" ? "Liever eerst een vraag stellen? " : "Prefer to ask a question first? "}
            <a href={`mailto:${aboutContent.contact.email}`}>{aboutContent.contact.email}</a>
          </p>
        </div>
      </div>

      <Footer appConfig={appConfig} t={t} />
        </div>
  )
}

export default LandingPage
